import { supabase } from '../supabase';
import { locationSlugToIlikePattern } from '../locationSlugFilter';
import { transformPropertySummaryToType, transformPropertySummariesToTypes } from './propertyTransformers';

export interface Property {
  id: string;
  title: string;
  description: string | null;
  property_type: string;
  room_type: string;
  max_guests: number;
  bedroom_count: number;
  bed_count: number;
  bathroom_count: number;
  location: {
    type: string;
    coordinates: [number, number];
  } | null;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    formatted?: string;
    full_address?: string;
    placeId?: string;
  };
  base_price: number;
  cleaning_fee: number | null;
  security_deposit: number | null;
  photos: string[] | null;
  status: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  owner_id: string;
}

export interface PropertySummary {
  id: string;
  title: string;
  property_type: string;
  status: string;
  price: number; // Changed from monthly_revenue to price for consistency
  bedroom_count: number;
  bathroom_count: number;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    formatted?: string;
    full_address?: string;
    placeId?: string;
  };
  location?: {
    type: string;
    coordinates: [number, number];
  } | null;
  owner_id: string; // Only owner_id is available, not full owner details
  created_at: string;
  updated_at: string;
  images: string[];
  amenities: string[];
}

export interface PropertyWithDetails extends Property {
  images: string[];
  amenities: string[];
  owner?: {
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string | null;
  };
}

/**
 * Interface that makes PropertySummary compatible with PropertyType
 * This eliminates the need for manual conversion functions
 */
export interface PropertySummaryForDisplay extends Omit<PropertySummary, 'location'> {
  // Map PropertySummary fields to PropertyType expectations
  bedrooms: number; // Maps from bedroom_count
  bathrooms: number; // Maps from bathroom_count
  features: string[]; // Maps from amenities
  propertyType: string; // Maps from property_type
  bond: number; // Default value
  parking: number; // Default value
  description: string; // Default value
  availableFrom: string; // Default value
  featured: boolean; // Default value
  deposit: boolean; // Default value
  location: {
    lat: number;
    lng: number;
    address: string;
    city: string;
    region: string;
  };
  agent: {
    name: string;
    phone: string;
    email: string;
    agency: string;
    photo: string;
  };
  inspections: {
    date: string;
    time: string;
  }[];
}

/**
 * Converts PropertySummary to PropertySummaryForDisplay
 * This eliminates the need for manual conversion in components
 */
export function convertPropertySummaryToDisplay(summary: PropertySummary): PropertySummaryForDisplay {
  return {
    ...summary,
    price: summary.price || 0, // Explicitly ensure price is passed through
    bedrooms: summary.bedroom_count,
    bathrooms: summary.bathroom_count,
    features: summary.amenities,
    propertyType: summary.property_type,
    bond: 0, // Default value
    parking: 0, // Default value
    description: '', // Summary view doesn't have description
    availableFrom: 'now',
    featured: true, // All properties from featured API are featured
    deposit: false,
    location: {
      lat: summary.location?.coordinates?.[1] || 0,
      lng: summary.location?.coordinates?.[0] || 0,
      address: summary.address?.street || '',
      city: summary.address?.city || '',
      region: summary.address?.state || ''
    },
    agent: {
      name: 'Property Owner', // Owner details not available in summary view
      phone: '+63 917 000 0000',
      email: 'owner@homeph.com',
      agency: 'Property Owner',
      photo: 'https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    inspections: []
  };
}

export interface CreatePropertyData {
  title: string;
  description?: string;
  property_type: string;
  room_type: string;
  max_guests: number;
  bedroom_count: number;
  bed_count: number;
  bathroom_count: number;
  base_price: number;
  cleaning_fee?: number;
  security_deposit?: number;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    formatted?: string;
    full_address?: string;
    placeId?: string;
  };
  location?: {
    type: string;
    coordinates: [number, number];
  } | null;
  status?: string;
  amenities?: string[];
  images?: File[];
}

export interface UpdatePropertyData extends Partial<CreatePropertyData> {
  id: string;
}

export interface PropertiesResponse {
  data: Property[] | null;
  error: string | null;
}

export interface PropertyResponse {
  data: Property | null;
  error: string | null;
}

/**
 * Prefer URLs from `property_images`; if none (empty array from RLS or truly no rows),
 * fall back to `properties.photos`. Avoids `[] || photos` never reaching photos.
 */
/** Normalize legacy `properties.photos` (jsonb array, text json, or single URL string). */
function urlsFromPhotosField(photos: string[] | string | null | undefined): string[] {
  if (photos == null) return [];
  if (Array.isArray(photos)) {
    return photos.filter((u): u is string => typeof u === 'string' && u.trim() !== '');
  }
  if (typeof photos === 'string') {
    const t = photos.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(t);
        if (Array.isArray(parsed)) {
          return parsed.filter((u): u is string => typeof u === 'string' && u.trim() !== '');
        }
      } catch {
        /* single malformed entry */
      }
    }
    return [t];
  }
  return [];
}

export function normalizePropertyListingImages(
  propertyImages: { url?: string | null }[] | null | undefined,
  photos: string[] | string | null | undefined
): string[] {
  const fromTable = (propertyImages ?? [])
    .map((img) => img?.url)
    .filter((u): u is string => typeof u === 'string' && u.trim() !== '');
  if (fromTable.length > 0) return fromTable;

  return urlsFromPhotosField(photos);
}

/** Supabase row shape for public listing queries that join images + amenities */
export interface PropertyListingQueryRow {
  id: string;
  title: string;
  property_type: string;
  status: string;
  base_price: number;
  bedroom_count: number;
  bathroom_count: number;
  address: PropertySummary['address'];
  photos?: string[] | string | null;
  location?: PropertySummary['location'] | null;
  created_at: string;
  updated_at: string;
  owner_id: string;
  property_images?: { url: string }[] | null;
  property_amenities?: { amenity?: { name?: string } | null }[] | null;
}

export const PROPERTY_LISTING_RELATIONS_SELECT = `
        id,
        title,
        property_type,
        status,
        base_price,
        bedroom_count,
        bathroom_count,
        address,
        photos,
        location,
        created_at,
        updated_at,
        owner_id,
        property_images(url, "order"),
        property_amenities(
          amenity:amenities(name)
        )
      `;

/** Lighter select for fallback listing (no amenity join) */
export const PROPERTY_LISTING_SIMPLE_SELECT = `
        id,
        title,
        property_type,
        status,
        base_price,
        bedroom_count,
        bathroom_count,
        address,
        photos,
        location,
        created_at,
        updated_at,
        owner_id,
        property_images(url, "order")
      `;

/**
 * Map a `properties` row (with optional joins) to PropertySummary.
 * Prefers `property_images` URLs, then falls back to `photos` (same as pre–summary-view listings).
 */
export function propertyListingJoinRowToSummary(
  row: PropertyListingQueryRow,
  options?: { includeAmenities?: boolean }
): PropertySummary {
  const includeAmenities = options?.includeAmenities !== false;
  const images = normalizePropertyListingImages(row.property_images, row.photos);
  const amenities = includeAmenities
    ? row.property_amenities
        ?.map((pa) => pa.amenity?.name)
        .filter((n): n is string => typeof n === 'string' && n.trim() !== '') ?? []
    : [];
  return {
    id: row.id,
    title: row.title,
    property_type: row.property_type,
    status: row.status,
    price: row.base_price,
    bedroom_count: row.bedroom_count,
    bathroom_count: row.bathroom_count,
    address: row.address,
    location: row.location ?? null,
    owner_id: row.owner_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
    images,
    amenities,
  };
}

/**
 * Parse `images` from `property_summary_view` (json/jsonb from json_agg of URLs).
 * Public listing uses this view so thumbnails match the detail page without RLS on nested rows.
 */
export function imagesFromPropertySummaryView(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((u): u is string => typeof u === 'string' && u.trim() !== '');
  }
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((u): u is string => typeof u === 'string' && u.trim() !== '');
      }
    } catch {
      const t = raw.trim();
      return t ? [t] : [];
    }
  }
  return [];
}

export function summaryViewRowToPropertySummary(row: Record<string, unknown>): PropertySummary {
  const amenitiesRaw = row.amenities;
  const amenities = Array.isArray(amenitiesRaw)
    ? (amenitiesRaw as unknown[]).filter((a): a is string => typeof a === 'string' && a.trim() !== '')
    : [];
  const price =
    typeof row.price === 'number'
      ? row.price
      : typeof row.monthly_revenue === 'number'
        ? row.monthly_revenue
        : Number(row.price ?? row.monthly_revenue ?? 0) || 0;

  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? ''),
    property_type: String(row.property_type ?? ''),
    status: String(row.status ?? ''),
    price,
    bedroom_count: Number(row.bedroom_count ?? 0),
    bathroom_count: Number(row.bathroom_count ?? 0),
    address: (row.address as PropertySummary['address']) ?? {},
    location: (row.location as PropertySummary['location']) ?? null,
    owner_id: String(row.owner_id ?? ''),
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    images: imagesFromPropertySummaryView(row.images),
    amenities,
  };
}

/**
 * Fetches all properties from the property_summary_view
 */
export async function getAllProperties(): Promise<PropertiesResponse> {
  try {
    const { data, error } = await supabase
      .from('property_summary_view')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching properties:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching properties:', error);
    return { data: null, error: 'Failed to fetch properties' };
  }
}

/**
 * Fetches a single property by ID with full details
 */
export async function getPropertyById(id: string): Promise<PropertyResponse> {
  try {
    // First try to get from property_summary_view
    const { data: summaryProperty, error: summaryError } = await supabase
      .from('property_summary_view')
      .select('*')
      .eq('id', id)
      .single();

    if (summaryProperty && !summaryError) {
      // Transform to Property format
      const property: Property = {
        id: summaryProperty.id,
        title: summaryProperty.title,
        description: null, // Will fetch from properties table
        property_type: summaryProperty.property_type,
        room_type: 'entire_place', // Default
        max_guests: 2, // Default
        bedroom_count: summaryProperty.bedroom_count,
        bed_count: summaryProperty.bedroom_count,
        bathroom_count: summaryProperty.bathroom_count,
        location: summaryProperty.location,
        address: summaryProperty.address,
        base_price: summaryProperty.price,
        cleaning_fee: null,
        security_deposit: null,
        photos: summaryProperty.images,
        status: summaryProperty.status,
        metadata: {},
        created_at: summaryProperty.created_at,
        updated_at: summaryProperty.updated_at,
        owner_id: summaryProperty.owner_id
      };

      // Fetch additional details from properties table
      const { data: dbProperty, error: dbError } = await supabase
        .from('properties')
        .select('description, room_type, max_guests, cleaning_fee, security_deposit')
        .eq('id', id)
        .single();

      if (dbProperty && !dbError) {
        property.description = dbProperty.description;
        property.room_type = dbProperty.room_type;
        property.max_guests = dbProperty.max_guests;
        property.cleaning_fee = dbProperty.cleaning_fee;
        property.security_deposit = dbProperty.security_deposit;
      }

      return { data: property, error: null };
    }

    // Fallback to properties table
    const { data: dbProperty, error: dbError } = await supabase
      .from('properties')
      .select('*')
      .eq('id', id)
      .single();

    if (dbError) {
      console.error('Error fetching property:', dbError);
      return { data: null, error: dbError.message };
    }

    return { data: dbProperty, error: null };
  } catch (error) {
    console.error('Error fetching property:', error);
    return { data: null, error: 'Failed to fetch property' };
  }
}

/**
 * Creates a new property with amenities and images
 */
export async function createProperty(propertyData: CreatePropertyData, ownerId: string): Promise<PropertyResponse> {
  try {
    // 1. Create the property record
    const { data: property, error: propertyError } = await supabase
      .from('properties')
      .insert({
        ...propertyData,
        owner_id: ownerId,
        status: propertyData.status || 'draft'
      })
      .select()
      .single();

    if (propertyError) throw propertyError;

    // 2. Create amenities if provided
    if (propertyData.amenities && propertyData.amenities.length > 0) {
      const amenityInserts = propertyData.amenities.map(async (amenityName) => {
        // Check if amenity exists, if not create it
        const { data: existingAmenity } = await supabase
          .from('amenities')
          .select('id')
          .eq('name', amenityName)
          .single();
        
        if (!existingAmenity) {
          // Create the amenity
          const { data: newAmenity, error: amenityError } = await supabase
            .from('amenities')
            .insert({
              name: amenityName,
              category_id: null,
              icon: 'check',
              description: `${amenityName} available`
            })
            .select()
            .single();
          
          if (amenityError) throw amenityError;
          return newAmenity.id;
        }
        
        return existingAmenity.id;
      });
      
      const amenityIds = await Promise.all(amenityInserts);
      
      // Link amenities to the property
      const propertyAmenityInserts = amenityIds.map(async (amenityId) => {
        const { error: linkError } = await supabase
          .from('property_amenities')
          .insert({
            property_id: property.id,
            amenity_id: amenityId
          });
        
        if (linkError) throw linkError;
      });
      
      await Promise.all(propertyAmenityInserts);
    }

    // 3. Handle images if provided
    if (propertyData.images && propertyData.images.length > 0) {
      // Note: Image upload should be handled by the calling component
      // This function assumes images are already uploaded and URLs are provided
      console.log('Images should be uploaded separately before calling this function');
    }

    return { data: property, error: null };
  } catch (error) {
    console.error('Error creating property:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Failed to create property' };
  }
}

/**
 * Updates an existing property
 */
export async function updateProperty(propertyData: UpdatePropertyData): Promise<PropertyResponse> {
  try {
    const { id, ...updateData } = propertyData;
    
    const { data: property, error } = await supabase
      .from('properties')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Update amenities if provided
    if (propertyData.amenities !== undefined) {
      // Remove existing amenities
      await supabase
        .from('property_amenities')
        .delete()
        .eq('property_id', id);

      // Add new amenities
      if (propertyData.amenities.length > 0) {
        const amenityInserts = propertyData.amenities.map(async (amenityName) => {
          // Check if amenity exists, if not create it
          const { data: existingAmenity } = await supabase
            .from('amenities')
            .select('id')
            .eq('name', amenityName)
            .single();
          
          if (!existingAmenity) {
            const { data: newAmenity, error: amenityError } = await supabase
              .from('amenities')
              .insert({
                name: amenityName,
                category_id: null,
                icon: 'check',
                description: `${amenityName} available`
              })
              .select()
              .single();
            
            if (amenityError) throw amenityError;
            return newAmenity.id;
          }
          
          return existingAmenity.id;
        });
        
        const amenityIds = await Promise.all(amenityInserts);
        
        // Link amenities to the property
        const propertyAmenityInserts = amenityIds.map(async (amenityId) => {
          const { error: linkError } = await supabase
            .from('property_amenities')
            .insert({
              property_id: id,
              amenity_id: amenityId
            });
          
          if (linkError) throw linkError;
        });
        
        await Promise.all(propertyAmenityInserts);
      }
    }

    return { data: property, error: null };
  } catch (error) {
    console.error('Error updating property:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Failed to update property' };
  }
}

/**
 * Deletes a property
 */
export async function deleteProperty(id: string): Promise<{ error: string | null }> {
  try {
    // Delete related records first
    await supabase
      .from('property_amenities')
      .delete()
      .eq('property_id', id);

    await supabase
      .from('property_images')
      .delete()
      .eq('property_id', id);

    // Delete the property
    const { error } = await supabase
      .from('properties')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error deleting property:', error);
    return { error: error instanceof Error ? error.message : 'Failed to delete property' };
  }
}

/**
 * Fetches properties by owner
 */
export async function getPropertiesByOwner(ownerId: string): Promise<PropertiesResponse> {
  try {
    const { data, error } = await supabase
      .from('property_summary_view')
      .select('*')
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching owner properties:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching owner properties:', error);
    return { data: null, error: 'Failed to fetch owner properties' };
  }
}

/**
 * Fetches active properties for public viewing
 */
export async function getActiveProperties(): Promise<PropertiesResponse> {
  try {
    const { data, error } = await supabase
      .from('property_summary_view')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching active properties:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching active properties:', error);
    return { data: null, error: 'Failed to fetch active properties' };
  }
}

/**
 * Searches properties with filters
 */
export async function searchProperties(filters: {
  search?: string;
  property_type?: string;
  min_price?: number;
  max_price?: number;
  bedrooms?: number;
  bathrooms?: number;
  amenities?: string[];
}): Promise<PropertiesResponse> {
  try {
    let query = supabase
      .from('property_summary_view')
      .select('*')
      .eq('status', 'active');

    // Apply filters
    if (filters.property_type) {
      query = query.eq('property_type', filters.property_type);
    }

    if (filters.min_price !== undefined) {
      query = query.gte('base_price', filters.min_price);
    }

    if (filters.max_price !== undefined) {
      query = query.lte('base_price', filters.max_price);
    }

    if (filters.bedrooms !== undefined) {
      query = query.eq('bedroom_count', filters.bedrooms);
    }

    if (filters.bathrooms !== undefined) {
      query = query.eq('bathroom_count', filters.bathrooms);
    }

    // Text search
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error('Error searching properties:', error);
      return { data: null, error: error.message };
    }

    // Filter by amenities if specified
    let filteredData = data;
    if (filters.amenities && filters.amenities.length > 0) {
      filteredData = data?.filter(property => {
        const propertyAmenities = property.amenities || [];
        return filters.amenities!.every(amenity => 
          propertyAmenities.includes(amenity)
        );
      }) || [];
    }

    return { data: filteredData, error: null };
  } catch (error) {
    console.error('Error searching properties:', error);
    return { data: null, error: 'Failed to search properties' };
  }
}

/**
 * Fetches featured properties for display on the homepage
 */
export async function getFeaturedProperties(): Promise<{ data: PropertySummary[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('property_summary_view')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(6); // Limit to 6 featured properties

    if (error) {
      console.error('Error fetching featured properties:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching featured properties:', error);
    return { data: null, error: 'Failed to fetch featured properties' };
  }
}

/**
 * Fetches all properties including both landlord and agent properties
 * This function uses an optimized single query for better performance
 */
export async function getAllPropertiesIncludingAgents(): Promise<{ data: PropertySummary[] | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('properties')
      .select(PROPERTY_LISTING_RELATIONS_SELECT)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching properties:', error);
      return { data: null, error: error.message };
    }

    const transformedProperties = (data || []).map((row) =>
      propertyListingJoinRowToSummary(row as PropertyListingQueryRow)
    );

    return { data: transformedProperties, error: null };
  } catch (error) {
    console.error('Error fetching all properties including agents:', error);
    return { data: null, error: 'Failed to fetch properties' };
  }
}

/**
 * Optimized function to fetch properties with server-side filtering and pagination
 * This provides much better performance for large datasets
 */
export async function getPropertiesOptimized(filters: {
  page?: number;
  limit?: number;
  search?: string;
  property_type?: string;
  min_price?: number;
  max_price?: number;
  bedrooms?: number;
  bathrooms?: number;
  amenities?: string[];
  sort_by?: 'created_at' | 'price' | 'bedrooms' | 'bathrooms';
  sort_order?: 'asc' | 'desc';
  /** URL param e.g. `quezon-city` — matched against JSON `address` fields */
  location_slug?: string | null;
} = {}): Promise<{ data: PropertySummary[] | null; total: number; error: string | null }> {
  try {
    const {
      page = 1,
      limit = 12,
      search,
      property_type,
      min_price,
      max_price,
      bedrooms,
      bathrooms,
      amenities,
      sort_by = 'created_at',
      sort_order = 'desc',
      location_slug
    } = filters;

    console.log('🔍 getPropertiesOptimized: Starting with filters:', filters);

    // Query `properties` with joins so we get property_images URLs and photos[] fallback.
    // property_summary_view.images only aggregates property_images; legacy photos-only listings would show placeholders.
    let query = supabase
      .from('properties')
      .select(PROPERTY_LISTING_RELATIONS_SELECT, { count: 'exact' })
      .eq('status', 'active');

    console.log('🔍 getPropertiesOptimized: Base query built (properties + images)');

    if (search) {
      query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
    }

    if (property_type) {
      query = query.eq('property_type', property_type);
    }

    if (min_price !== undefined) {
      query = query.gte('base_price', min_price);
    }

    if (max_price !== undefined) {
      query = query.lte('base_price', max_price);
    }

    if (bedrooms !== undefined) {
      query = query.eq('bedroom_count', bedrooms);
    }

    if (bathrooms !== undefined) {
      query = query.eq('bathroom_count', bathrooms);
    }

    const locationPattern = location_slug ? locationSlugToIlikePattern(location_slug) : null;
    if (locationPattern) {
      query = query.or(
        `address->>city.ilike.${locationPattern},address->>state.ilike.${locationPattern},address->>street.ilike.${locationPattern},address->>formatted.ilike.${locationPattern}`
      );
    }

    if (sort_by === 'price') {
      query = query.order('base_price', { ascending: sort_order === 'asc' });
    } else if (sort_by === 'bedrooms') {
      query = query.order('bedroom_count', { ascending: sort_order === 'asc' });
    } else if (sort_by === 'bathrooms') {
      query = query.order('bathroom_count', { ascending: sort_order === 'asc' });
    } else {
      query = query.order('created_at', { ascending: sort_order === 'asc' });
    }

    // Apply pagination
    const start = (page - 1) * limit;
    const end = start + limit - 1;
    query = query.range(start, end);

    console.log('🔍 getPropertiesOptimized: Executing query with pagination:', { start, end, limit });

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ getPropertiesOptimized: Query error:', error);
      return { data: null, total: 0, error: error.message };
    }

    console.log('✅ getPropertiesOptimized: Query successful, raw data count:', data?.length || 0);
    console.log('📊 getPropertiesOptimized: Total count from database:', count);

    const transformedProperties = (data || []).map((row) =>
      propertyListingJoinRowToSummary(row as PropertyListingQueryRow)
    );

    console.log('✅ getPropertiesOptimized: Transformed properties count:', transformedProperties.length);

    // Filter by amenities if specified (client-side for now, can be optimized later)
    let finalProperties = transformedProperties;
    if (amenities && amenities.length > 0) {
      finalProperties = transformedProperties.filter(property => {
        const propertyAmenities = property.amenities || [];
        return amenities.every(amenity => 
          propertyAmenities.includes(amenity)
        );
      });
      console.log('🔍 getPropertiesOptimized: After amenities filter:', finalProperties.length);
    }

    console.log('🎯 getPropertiesOptimized: Final result:', { 
      dataCount: finalProperties.length, 
      total: count || 0 
    });

    return { 
      data: finalProperties, 
      total: count || 0, 
      error: null 
    };
  } catch (error) {
    console.error('❌ getPropertiesOptimized: Unexpected error:', error);
    return { data: null, total: 0, error: 'Failed to fetch properties' };
  }
}

/**
 * Simple, reliable function to fetch properties without complex joins
 * This is a fallback function that should always work
 */
export async function getPropertiesSimple(filters: {
  page?: number;
  limit?: number;
  property_type?: string;
  min_price?: number;
  max_price?: number;
  bedrooms?: number;
  bathrooms?: number;
  location_slug?: string | null;
} = {}): Promise<{ data: PropertySummary[] | null; total: number; error: string | null }> {
  try {
    const {
      page = 1,
      limit = 12,
      property_type,
      min_price,
      max_price,
      bedrooms,
      bathrooms,
      location_slug
    } = filters;

    console.log('🔍 getPropertiesSimple: Starting with filters:', filters);

    let query = supabase
      .from('properties')
      .select(PROPERTY_LISTING_SIMPLE_SELECT, { count: 'exact' })
      .eq('status', 'active');

    console.log('🔍 getPropertiesSimple: Base query built (properties + images)');

    if (property_type) {
      query = query.eq('property_type', property_type);
    }

    if (min_price !== undefined) {
      query = query.gte('base_price', min_price);
    }

    if (max_price !== undefined) {
      query = query.lte('base_price', max_price);
    }

    if (bedrooms !== undefined) {
      query = query.eq('bedroom_count', bedrooms);
    }

    if (bathrooms !== undefined) {
      query = query.eq('bathroom_count', bathrooms);
    }

    const simpleLocationPattern = location_slug ? locationSlugToIlikePattern(location_slug) : null;
    if (simpleLocationPattern) {
      query = query.or(
        `address->>city.ilike.${simpleLocationPattern},address->>state.ilike.${simpleLocationPattern},address->>street.ilike.${simpleLocationPattern},address->>formatted.ilike.${simpleLocationPattern}`
      );
    }

    query = query.order('created_at', { ascending: false });

    // Apply pagination
    const start = (page - 1) * limit;
    const end = start + limit - 1;
    query = query.range(start, end);

    console.log('🔍 getPropertiesSimple: Executing query with pagination:', { start, end, limit });

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ getPropertiesSimple: Query error:', error);
      return { data: null, total: 0, error: error.message };
    }

    console.log('✅ getPropertiesSimple: Query successful, raw data count:', data?.length || 0);
    console.log('📊 getPropertiesSimple: Total count from database:', count);

    const transformedProperties = (data || []).map((row) =>
      propertyListingJoinRowToSummary(row as PropertyListingQueryRow, { includeAmenities: false })
    );

    console.log('✅ getPropertiesSimple: Transformed properties count:', transformedProperties.length);

    return { 
      data: transformedProperties, 
      total: count || 0, 
      error: null 
    };
  } catch (error) {
    console.error('❌ getPropertiesSimple: Unexpected error:', error);
    return { data: null, total: 0, error: 'Failed to fetch properties' };
  }
}
